import { useEffect, useRef } from "react";

import { FormProvider } from "react-hook-form";

import Box from "@/components/Box";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import Center from "@/tpo/Center";
import ChevronComponent from "@/tpo/Chevron";
import ControlledCheckboxGroup from "@/tpo/ControlledCheckboxGroup";
import ControlledFormField from "@/tpo/ControlledFormField";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import Stack from "@/tpo/Stack";
import ButtonV2, { IndicativeButton } from "@/v2/Buttons";
import { gql } from "@apollo/client";

const UPDATE_CONTACT_EMAIL_MUTATION = gql`
  mutation UpdateContactEmailMutation($input: UpdateContactEmailMutationInput!) {
    updateContactEmailMutation(input: $input) {
      organisation {
        id
        additionalNotificationEmail
        additionalNotificationEmailForNotifications
      }
      errors {
        field
        messages
      }
    }
  }
`;

export default function UpdateContactEmailForm() {
  const { organisation } = useOrganisationContext();
  const ref = useRef();

  const api = useDjangoGraphqlForm({
    mutation: UPDATE_CONTACT_EMAIL_MUTATION,
    mutationName: "updateContactEmailMutation",
    defaultValues: {
      email: organisation?.additionalNotificationEmail || "",
      notifications: organisation?.additionalNotificationEmailForNotifications || []
    },
    transformer: data => ({
      ...data,
      id: organisation?.id
    }),
    handleSuccess: () => {
      if (ref.current) {
        ref.current.setSuccessful(true);
        ref.current.setPending(false);
      }
    },
    handleFailure: () => {
      if (ref.current) {
        ref.current.setSuccessful(false);
        ref.current.setPending(false);
      }
    }
  });

  const { reset } = api.methods;

  useEffect(() => {
    if (organisation?.additionalNotificationEmail !== undefined) {
      reset({
        email: organisation?.additionalNotificationEmail || "",
        notifications: organisation?.additionalNotificationEmailForNotifications || []
      });
    }
  }, [
    organisation?.additionalNotificationEmail,
    organisation?.additionalNotificationEmailForNotifications,
    reset
  ]);

  const { isDirty } = api.methods.formState;

  return (
    <FormProvider {...api.methods}>
      <Stack
        as="form"
        gap={30}
        onSubmit={api.methods.handleSubmit(api.onSubmit)}
        mx="auto"
        maxWidth={720}
        width="100%"
        px={20}
      >
        <Stack gap={20}>
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
            Organisation notification
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
            By default, all notifications are sent to the organisation owner’s email address.
            Ordering practitioners also receive notifications for their own orders and results.
            <br />
            <br />
            If you’d like some or all notifications to be sent to an additional email address (for
            example, a finance or admin inbox), please enter the address below and select which
            emails should receive a copy.
          </Box>
        </Stack>
        <Stack gap={20}>
          <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]}>
            Additional email address
          </Box>
          <ControlledFormField name="email" Component={FloatingLabelInput} label="Email address" />
        </Stack>
        <ControlledCheckboxGroup
          name="notifications"
          values={[
            {
              label: "Daily summary (orders, results, payments)",
              value: "Daily summary"
            },
            {
              label: "Individual order notifications",
              value: "Order notifications"
            },
            {
              label: "Individual result notifications",
              value: "Result notifications"
            }
          ]}
          label="Choose the notifications that this email will receive"
          labelProps={{
            fontFamily: "gilroyBold",
            fontSize: [16, 16, 18],
            mb: 28
          }}
          checkboxLabelProps={{
            fontFamily: "gilroyMedium",
            fontSize: 14
          }}
          Container={Stack}
          containerProps={{
            gap: 28
          }}
        />
        {isDirty && (
          <Center gap={20}>
            <ButtonV2
              type="button"
              size={["sm", "sm", "md"]}
              color="red"
              onClick={() => {
                api.methods.reset();
              }}
            >
              Cancel
            </ButtonV2>
            <IndicativeButton
              rightIcon={<ChevronComponent />}
              ref={ref}
              defaultColor="green"
              pendingColor="#0cf970"
              successColor="#2ecc71"
              failureColor="error"
              size={["sm", "sm", "md"]}
              disabled={api.loading}
            >
              save
            </IndicativeButton>
          </Center>
        )}
      </Stack>
    </FormProvider>
  );
}
